import {
  CanChange,
  InsuranceDetails,
  InsuranceDetailsView,
} from 'Api/insurance';
import { Brand, PartnerUrl } from 'App/themes/themes.types';
import {
  isAkaCoInsuredAndNotAllowedToChange,
  isAutoAndNotAllowedToChange,
  isFireAndHouseNatureAndNotAllowedToChange,
} from 'Components/InsuranceDetailsSections/helpers/insuranceDetailsSections.helpers';

import { InsuranceStatus } from 'Constants/insuranceStatus.constants';
import { ProductName } from 'Constants/products.constants';
import { Dispatch, SetStateAction } from 'react';
import { NavLink } from 'react-router';
import {
  isUnderCancellation,
  isUnderChange,
} from 'Utils/insuranceStatus/insuranceStatus.utils';

export const shouldShowChangeRequestModal = (
  insuranceDetailsView: InsuranceDetailsView,
  hasChangeForm: boolean,
  hasChangeUnderRenewalAndUnderChangeLogic: boolean,
  isInRenewal: boolean,
): boolean => {
  if (hasChangeUnderRenewalAndUnderChangeLogic) {
    return false;
  }

  return (
    !hasChangeForm || isUnderActivation(insuranceDetailsView) || isInRenewal
  );
};

export const shouldShowNotAllowedToChangeModal = (
  insuranceDetailsView: InsuranceDetailsView,
  hasChangeForm: boolean,
  hasChangeUnderRenewalAndUnderChangeLogic: boolean,
  currentInsuranceStatus: InsuranceStatus,
  isInRenewal: boolean,
): boolean => {
  return (
    !hasChangeForm ||
    isUnderActivation(insuranceDetailsView) ||
    (isNotAllowedToChange(insuranceDetailsView) &&
      isUnderChangeAndHasNotChangeUnderRenewalAndUnderChangeLogic(
        insuranceDetailsView,
        hasChangeUnderRenewalAndUnderChangeLogic,
        currentInsuranceStatus,
        isInRenewal,
      ))
  );
};

const isUnderChangeAndHasNotChangeUnderRenewalAndUnderChangeLogic = (
  insuranceDetailsView: InsuranceDetailsView,
  hasChangeUnderRenewalAndUnderChangeLogic: boolean,
  currentInsuranceStatus: InsuranceStatus,
  isInRenewal: boolean,
): boolean => {
  return (
    isUnderChange(currentInsuranceStatus) &&
    !isInRenewal &&
    !hasChangeUnderRenewalAndUnderChangeLogic
  );
};

export const isNotAllowedToChange = (
  insuranceDetailsView: InsuranceDetailsView,
): boolean =>
  insuranceDetailsView.canChange === CanChange.NOT_ALLOW ||
  isFireAndHouseNatureAndNotAllowedToChange(
    insuranceDetailsView.coverage,
    insuranceDetailsView.riskList,
  );

export const isUnderActivation = (
  insuranceDetailsView: InsuranceDetailsView,
): boolean =>
  insuranceDetailsView.insuranceStatus === InsuranceStatus.UNDER_ACTIVATION;

export const getChangeInsuranceButtonProps = (
  modalType: 'changeRequest' | 'notAllowedToChange',
  shouldUseModal: boolean,
  setShowChangeRequestModal: Dispatch<SetStateAction<boolean>>,
  setShowNotAllowedToChangeModal: Dispatch<SetStateAction<boolean>>,
  policyNumber: string,
  toggleRebrand: boolean,
) => {
  const notAllowedToChange = {
    onClick: () => setShowNotAllowedToChangeModal(true),
  };
  const changeRequest = { onClick: () => setShowChangeRequestModal(true) };
  const actionProps =
    modalType === 'changeRequest' ? changeRequest : notAllowedToChange;

  const changePageProps = toggleRebrand
    ? { to: `/avtale/${policyNumber}/endre?beta=1`, as: NavLink }
    : { to: `/avtale/${policyNumber}/endre`, as: NavLink };

  return shouldUseModal ? actionProps : changePageProps;
};

export const shouldDisableChanges = (
  insuranceDetailsView: InsuranceDetailsView,
  hasChangeForm: boolean,
  insuranceDetails: InsuranceDetails,
): boolean =>
  isUnderChangeAndHasChangeForm(insuranceDetailsView, hasChangeForm) ||
  isUnderCancellation(insuranceDetailsView.insuranceStatus) ||
  isAutoAndNotAllowedToChange(insuranceDetails) ||
  isAkaCoInsuredAndNotAllowedToChange(insuranceDetailsView);

const isUnderChangeAndHasChangeForm = (
  insuranceDetailsView: InsuranceDetailsView,
  hasChangeForm: boolean,
): boolean => {
  return isUnderChange(insuranceDetailsView.insuranceStatus) && hasChangeForm;
};

// deregistered vehicles with delkasko as coverage are not allowed to change the
// insurance as delkasko is the lowest allowed coverage for a deregistered vehicle
export function isAllowedToChange(
  insuranceDetails: InsuranceDetailsView,
): boolean {
  const { isDeregistered, coverage } = insuranceDetails;
  return !(isDeregistered && coverage === 'delkasko');
}

// pregnant and newborn should not be cancellable online
export function isAllowedToCancel(
  insuranceDetails: InsuranceDetailsView,
): boolean {
  return insuranceDetails.insuranceProduct !== ProductName.PREGNANT_AND_NEWBORN;
}

export const getBrandedBasePath = (partner: string): string => {
  switch (partner) {
    case Brand.AKADEMIKERNE:
      return PartnerUrl.AKADEMIKERNE;
    case Brand.COOP:
      return PartnerUrl.COOP;
    case Brand.DANSKE_BANK:
      return PartnerUrl.DANSKE_BANK;
    case Brand.HUSEIERNE:
      return PartnerUrl.HUSEIERNE;
    default:
      return PartnerUrl.STB;
  }
};
